// src/components/Affiliates/Affiliate.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import './Affiliate.css';

const Affiliate = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [affiliateData, setAffiliateData] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [affiliateStats, setAffiliateStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0,
    pendingEarnings: 0
  });
  const [recentReferrals, setRecentReferrals] = useState([]);
  const [referralLink, setReferralLink] = useState('');

  // Login form state
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });

  // Signup form state
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    howDidYouHear: '',
    whyJoin: '',
    agreeTerms: false
  });

  // Form errors
  const [formErrors, setFormErrors] = useState({});
  const [loginErrors, setLoginErrors] = useState({});

  // Commission rate
  const COMMISSION_RATE = 0.05; // 5%

  // Check if user is already logged in (session)
  useEffect(() => {
    const savedAffiliate = sessionStorage.getItem('affiliateData');
    if (savedAffiliate) {
      const data = JSON.parse(savedAffiliate);
      setAffiliateData(data);
      setIsLoggedIn(true);
      setReferralLink(`${window.location.origin}/register?ref=${data.referral_code}`);
      fetchAffiliateStats(data.referral_code);
      fetchRecentReferrals(data.referral_code);
    }
  }, []);

  // Fetch affiliate statistics
  const fetchAffiliateStats = async (referralCode) => {
    try {
      const { data: purchases, error } = await supabase
        .from('affiliate_purchases')
        .select('amount, status')
        .eq('referral_code', referralCode);

      if (error) {
        console.error('Error fetching stats:', error);
        return;
      }

      const totalReferrals = purchases?.length || 0;
      const totalEarnings = purchases?.reduce((sum, p) => sum + (p.amount || 0), 0) * COMMISSION_RATE;
      const pendingEarnings = purchases?.filter(p => p.status === 'pending')
        .reduce((sum, p) => sum + (p.amount || 0), 0) * COMMISSION_RATE;

      setAffiliateStats({
        totalReferrals: totalReferrals,
        totalEarnings: totalEarnings || 0,
        pendingEarnings: pendingEarnings || 0
      });

    } catch (error) {
      console.error('Error fetching affiliate stats:', error);
    }
  };

  // Fetch recent referrals
  const fetchRecentReferrals = async (referralCode) => {
    try {
      console.log('🔍 Fetching referrals for code:', referralCode);
      
      const { data, error } = await supabase
        .from('affiliate_purchases')
        .select('*')
        .eq('referral_code', referralCode)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('❌ Error fetching referrals:', error);
        setRecentReferrals([]);
        return;
      }

      console.log('✅ Found referrals:', data);
      setRecentReferrals(data || []);

    } catch (error) {
      console.error('❌ Error fetching recent referrals:', error);
      setRecentReferrals([]);
    }
  };

  // Handle login input changes
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    if (loginErrors[name]) {
      setLoginErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle signup input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate login
  const validateLogin = () => {
    const errors = {};
    let isValid = true;

    if (!loginData.username.trim()) {
      errors.username = 'Username is required';
      isValid = false;
    }
    if (!loginData.password) {
      errors.password = 'Password is required';
      isValid = false;
    }

    setLoginErrors(errors);
    return isValid;
  };

  // Validate signup
  const validateSignup = () => {
    const errors = {};
    let isValid = true;

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
      isValid = false;
    }
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
      isValid = false;
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
      isValid = false;
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }
    if (!formData.password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      isValid = false;
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    }
    if (!formData.agreeTerms) {
      errors.agreeTerms = 'You must agree to the terms';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    setIsSubmitting(true);
    setLoginError('');

    try {
      const { data, error } = await supabase
        .from('affiliates')
        .select('*')
        .or(`username.eq.${loginData.username},email.eq.${loginData.username}`)
        .single();

      if (error || !data) {
        setLoginError('Invalid username or password');
        setIsSubmitting(false);
        return;
      }

      if (data.password_hash === loginData.password) {
        const affiliateInfo = {
          id: data.id,
          full_name: data.full_name,
          username: data.username,
          email: data.email,
          referral_code: data.referral_code,
          created_at: data.created_at
        };

        sessionStorage.setItem('affiliateData', JSON.stringify(affiliateInfo));
        setAffiliateData(affiliateInfo);
        setIsLoggedIn(true);
        setReferralLink(`${window.location.origin}/register?ref=${data.referral_code}`);
        setShowLogin(false);
        fetchAffiliateStats(data.referral_code);
        fetchRecentReferrals(data.referral_code);

        await supabase
          .from('affiliates')
          .update({ last_login: new Date().toISOString() })
          .eq('id', data.id);

      } else {
        setLoginError('Invalid username or password');
      }

    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate sequential referral code
  const generateReferralCode = async () => {
    try {
      const { count, error } = await supabase
        .from('affiliates')
        .select('*', { count: 'exact', head: true });

      if (error) throw error;

      const nextNumber = (count || 0) + 1;
      return `AFF${String(nextNumber).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating referral code:', error);
      return `AFF${Date.now().toString().slice(-6)}`;
    }
  };

  // Handle signup submission
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;

    setIsSubmitting(true);

    try {
      const { data: existingUsername } = await supabase
        .from('affiliates')
        .select('username')
        .eq('username', formData.username)
        .single();

      if (existingUsername) {
        setFormErrors(prev => ({ ...prev, username: 'Username already taken' }));
        setIsSubmitting(false);
        return;
      }

      const { data: existingEmail } = await supabase
        .from('affiliates')
        .select('email')
        .eq('email', formData.email)
        .single();

      if (existingEmail) {
        setFormErrors(prev => ({ ...prev, email: 'Email already registered' }));
        setIsSubmitting(false);
        return;
      }

      const referralCode = await generateReferralCode();

      const { data, error } = await supabase
        .from('affiliates')
        .insert([{
          full_name: formData.fullName,
          username: formData.username.toLowerCase(),
          email: formData.email.toLowerCase(),
          password_hash: formData.password,
          phone: formData.phone,
          how_did_you_hear: formData.howDidYouHear,
          why_join: formData.whyJoin,
          referral_code: referralCode,
          status: 'active',
          commission_rate: COMMISSION_RATE,
          total_clicks: 0,
          total_referrals: 0,
          total_earnings: 0
        }])
        .select();

      if (error) throw error;

      const affiliateInfo = {
        id: data[0].id,
        full_name: data[0].full_name,
        username: data[0].username,
        email: data[0].email,
        referral_code: data[0].referral_code,
        created_at: data[0].created_at
      };

      sessionStorage.setItem('affiliateData', JSON.stringify(affiliateInfo));
      setAffiliateData(affiliateInfo);
      setIsLoggedIn(true);
      setReferralLink(`${window.location.origin}/register?ref=${data[0].referral_code}`);
      setShowSignUp(false);
      fetchAffiliateStats(data[0].referral_code);
      fetchRecentReferrals(data[0].referral_code);

      alert('✅ Account created successfully! Welcome to the AICIS Affiliate Program.');

    } catch (error) {
      console.error('Signup error:', error);
      alert('❌ Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem('affiliateData');
    setIsLoggedIn(false);
    setAffiliateData(null);
    setReferralLink('');
    setAffiliateStats({
      totalReferrals: 0,
      totalEarnings: 0,
      pendingEarnings: 0
    });
    setRecentReferrals([]);
  };

  // Copy referral link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    alert('✅ Referral link copied to clipboard!');
  };

  // ===== RENDER LOGIN MODAL =====
  const renderLoginModal = () => (
    <div className="affiliate-login-overlay" onClick={() => setShowLogin(false)}>
      <div className="affiliate-login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setShowLogin(false)}>×</button>
        
        <div className="login-header">
          <span className="login-badge">🔐 AFFILIATE LOGIN</span>
          <h2>Welcome Back</h2>
          <p>Login to your affiliate dashboard</p>
        </div>

        {loginError && (
          <div className="login-error">
            <span>⚠️</span> {loginError}
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label>Username or Email *</label>
            <input
              type="text"
              name="username"
              placeholder="Enter your username or email"
              value={loginData.username}
              onChange={handleLoginChange}
              className={loginErrors.username ? 'input-error' : ''}
              required
            />
            {loginErrors.username && <span className="error-message">{loginErrors.username}</span>}
          </div>

          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              name="password"
              placeholder="Enter your password"
              value={loginData.password}
              onChange={handleLoginChange}
              className={loginErrors.password ? 'input-error' : ''}
              required
            />
            {loginErrors.password && <span className="error-message">{loginErrors.password}</span>}
          </div>

          <button type="submit" className="login-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>

          <p className="login-footer">
            Don't have an account? <span className="login-link" onClick={() => { setShowLogin(false); setShowSignUp(true); }}>Sign up here</span>
          </p>
        </form>
      </div>
    </div>
  );

  // ===== RENDER SIGNUP MODAL =====
  const renderSignUpModal = () => (
    <div className="affiliate-signup-overlay" onClick={() => setShowSignUp(false)}>
      <div className="affiliate-signup-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setShowSignUp(false)}>×</button>
        
        <div className="signup-header">
          <span className="signup-badge">🚀 JOIN THE PROGRAM</span>
          <h2>Become an Affiliate</h2>
          <p>Create your account and start earning commissions</p>
        </div>

        <form onSubmit={handleSignup} className="signup-form">
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="fullName"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={handleInputChange}
              className={formErrors.fullName ? 'input-error' : ''}
              required
            />
            {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
          </div>

          <div className="form-group">
            <label>Username *</label>
            <input
              type="text"
              name="username"
              placeholder="Choose a unique username (min 3 characters)"
              value={formData.username}
              onChange={handleInputChange}
              className={formErrors.username ? 'input-error' : ''}
              required
            />
            {formErrors.username && <span className="error-message">{formErrors.username}</span>}
            <small className="form-hint">This will be used for login (e.g., john_affiliate)</small>
          </div>

          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              name="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleInputChange}
              className={formErrors.email ? 'input-error' : ''}
              required
            />
            {formErrors.email && <span className="error-message">{formErrors.email}</span>}
          </div>

          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              name="password"
              placeholder="Create a password (min 6 characters)"
              value={formData.password}
              onChange={handleInputChange}
              className={formErrors.password ? 'input-error' : ''}
              required
            />
            {formErrors.password && <span className="error-message">{formErrors.password}</span>}
          </div>

          <div className="form-group">
            <label>Confirm Password *</label>
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={formErrors.confirmPassword ? 'input-error' : ''}
              required
            />
            {formErrors.confirmPassword && <span className="error-message">{formErrors.confirmPassword}</span>}
          </div>

          <div className="form-group">
            <label>Phone Number *</label>
            <input
              type="tel"
              name="phone"
              placeholder="Enter your phone number"
              value={formData.phone}
              onChange={handleInputChange}
              className={formErrors.phone ? 'input-error' : ''}
              required
            />
            {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
          </div>

          <div className="form-group">
            <label>How did you hear about us?</label>
            <select
              name="howDidYouHear"
              value={formData.howDidYouHear}
              onChange={handleInputChange}
            >
              <option value="">Select an option</option>
              <option value="social_media">Social Media</option>
              <option value="email">Email</option>
              <option value="friend">Friend/Colleague</option>
              <option value="website">Website</option>
              <option value="event">Event</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Why do you want to join?</label>
            <textarea
              name="whyJoin"
              placeholder="Tell us why you're interested in becoming an affiliate"
              value={formData.whyJoin}
              onChange={handleInputChange}
              rows={3}
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleInputChange}
              />
              <span>I agree to the affiliate terms and conditions *</span>
            </label>
            {formErrors.agreeTerms && <span className="error-message">{formErrors.agreeTerms}</span>}
          </div>

          <button type="submit" className="signup-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Creating Account...' : 'Create Affiliate Account'}
          </button>

          <p className="signup-footer">
            Already have an account? <span className="signup-link" onClick={() => { setShowSignUp(false); setShowLogin(true); }}>Login here</span>
          </p>
        </form>
      </div>
    </div>
  );

  // ===== RENDER DASHBOARD =====
  const renderDashboard = () => (
    <div className="affiliate-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-welcome">
          <h2>👋 Welcome back, {affiliateData?.full_name}!</h2>
          <p className="dashboard-subtitle">Affiliate since {affiliateData?.created_at ? new Date(affiliateData.created_at).toLocaleDateString() : 'Today'}</p>
        </div>
        <div className="dashboard-actions">
          <button onClick={handleLogout} className="logout-btn">🚪 Logout</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <span className="stat-number">{affiliateStats.totalReferrals}</span>
          <span className="stat-label">Total Referrals</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">₦{affiliateStats.totalEarnings.toLocaleString()}</span>
          <span className="stat-label">Total Earnings</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">₦{affiliateStats.pendingEarnings.toLocaleString()}</span>
          <span className="stat-label">Pending Payment</span>
        </div>
      </div>

      {/* Referral Link */}
      <div className="referral-link-section">
        <p className="referral-link-label">🔗 Your Unique Referral Link:</p>
        <div className="referral-link-box">
          <input type="text" value={referralLink} readOnly />
          <button onClick={copyToClipboard} className="copy-btn">📋 Copy</button>
        </div>
        <p className="referral-instruction">Share this link with your network to earn 5% commission! And Always Refresh Your Page For Updates</p>
      </div>

      {/* Recent Referrals Table */}
      <div className="recent-referrals">
        <h3>📋 Recent Referrals</h3>
        {recentReferrals.length === 0 ? (
          <p className="no-referrals">No referrals yet. Start sharing your link!</p>
        ) : (
          <div className="referrals-table-wrapper">
            <table className="referrals-table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Organization</th>
                  <th>Package</th>
                  <th>Amount</th>
                  <th>Commission</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentReferrals.map((ref, index) => (
                  <tr key={index}>
                    <td className="td-name">{ref.full_name || 'Anonymous'}</td>
                    <td className="td-email">{ref.email || 'N/A'}</td>
                    <td className="td-org">{ref.organization || 'N/A'}</td>
                    <td className="td-package">{ref.package_title || 'N/A'}</td>
                    <td className="td-amount">₦{(ref.amount || 0).toLocaleString()}</td>
                    <td className="td-commission">₦{((ref.amount || 0) * 0.05).toLocaleString()}</td>
                    <td className="td-status">
                      <span className={`status-badge ${ref.status || 'pending'}`}>
                        {ref.status || 'pending'}
                      </span>
                    </td>
                    <td className="td-date">{new Date(ref.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );


  // ===== RENDER OVERVIEW TAB (For non-logged in users) =====
  const renderOverview = () => (
    <div className="affiliate-overview">
      <div className="overview-grid">
        <div className="overview-card">
          <div className="overview-icon">💰</div>
          <h3>5% Commission</h3>
          <p>Earn 5% commission on every successful referral for all packages</p>
        </div>
        <div className="overview-card">
          <div className="overview-icon">🏆</div>
          <h3>High-Value Packages</h3>
          <p>Platinum, Diamond, and Gold packages up to ₦7,650,000 with substantial earning potential</p>
        </div>
        <div className="overview-card">
          <div className="overview-icon">🌍</div>
          <h3>Global Opportunity</h3>
          <p>Promote Africa's premier infrastructure and climate change summit to a global audience</p>
        </div>
        <div className="overview-card">
          <div className="overview-icon">🤝</div>
          <h3>Full Support</h3>
          <p>Dedicated affiliate team providing marketing materials and guidance for your success</p>
        </div>
      </div>

      <div className="how-you-earn">
        <h2>How You Earn!</h2>
        <div className="earn-grid">
          <div className="earn-card">
            <div className="earn-percent">5%</div>
            <h4>All Packages</h4>
            <p>Earn 5% commission on ALL successful referrals - Sponsorship, Exhibition, and Participation packages</p>
          </div>
          <div className="earn-card">
            <div className="earn-percent">5%</div>
            <h4>Bulk Participant</h4>
            <p>5 and Above Participants at One Time using your Unique Affiliate Link</p>
          </div>
          <div className="earn-card">
            <div className="earn-percent">5%</div>
            <h4>Single Participant</h4>
            <p>1 Participant Package per time using your Unique Affiliate Link</p>
          </div>
        </div>
      </div>

      <div className="affiliate-cta">
        <h2>Ready to Start Earning?</h2>
        <p>Join the AICIS Affiliate Program today and earn 5% commission on all referrals</p>
        <div className="cta-buttons">
          <button onClick={() => setShowSignUp(true)} className="affiliate-cta-btn primary">
            Sign Up Now
          </button>
          <button onClick={() => setShowLogin(true)} className="affiliate-cta-btn secondary">
            Login
          </button>
        </div>
      </div>
    </div>
  );

  // ===== RENDER PACKAGES TAB =====
  const renderPackages = () => (
    <div className="packages-reference">
      <h2>Packages You Can Promote</h2>
      <p className="packages-subtitle">Share these packages with your network and earn 5% commission</p>

      <div className="package-category">
        <h3>Sponsorship Packages</h3>
        <div className="package-cards">
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-1.png" alt="Full Title" />
            </div>
            <h4>Full Title Sponsorship</h4>
            <div className="package-price">₦50,000,000</div>
            <div className="package-commission">💰 Commission: ₦2,500,000 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-2.png" alt="Co-Title" />
            </div>
            <h4>Co-Title Sponsorship</h4>
            <div className="package-price">₦25,000,000</div>
            <div className="package-commission">💰 Commission: ₦1,250,000 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-3.png" alt="Companies" />
            </div>
            <h4>Companies | Institutions</h4>
            <div className="package-price">₦10,000,000</div>
            <div className="package-commission">💰 Commission: ₦500,000 (5%)</div>
          </div>
        </div>
      </div>

      <div className="package-category">
        <h3>Exhibition Packages</h3>
        <div className="package-cards">
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-5.png" alt="Platinum" />
            </div>
            <h4>Platinum Exhibition</h4>
            <div className="package-price">₦7,650,000</div>
            <div className="package-commission">💰 Commission: ₦382,500 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-6.png" alt="Diamond" />
            </div>
            <h4>Diamond Exhibition</h4>
            <div className="package-price">₦5,950,000</div>
            <div className="package-commission">💰 Commission: ₦297,500 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-7.png" alt="Gold" />
            </div>
            <h4>Gold Exhibition</h4>
            <div className="package-price">₦4,250,000</div>
            <div className="package-commission">💰 Commission: ₦212,500 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-8.png" alt="Corporate" />
            </div>
            <h4>Corporate Bodies</h4>
            <div className="package-price">₦1,500,000</div>
            <div className="package-commission">💰 Commission: ₦75,000 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-9.png" alt="Government" />
            </div>
            <h4>Government Agencies</h4>
            <div className="package-price">₦1,000,000</div>
            <div className="package-commission">💰 Commission: ₦50,000 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-10.png" alt="Prime" />
            </div>
            <h4>Prime Exhibition</h4>
            <div className="package-price">₦500,000</div>
            <div className="package-commission">💰 Commission: ₦25,000 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-11.png" alt="Standard" />
            </div>
            <h4>Standard Exhibition</h4>
            <div className="package-price">₦300,000</div>
            <div className="package-commission">💰 Commission: ₦15,000 (5%)</div>
          </div>
        </div>
      </div>

      <div className="package-category">
        <h3>Participation Packages</h3>
        <div className="package-cards">
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-12.png" alt="International" />
            </div>
            <h4>International Attendee</h4>
            <div className="package-price">₦2,890,000</div>
            <div className="package-commission">💰 Commission: ₦144,500 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-13.png" alt="1 Day" />
            </div>
            <h4>Local SMEs | NGOs (1 Day)</h4>
            <div className="package-price">₦150,000</div>
            <div className="package-commission">💰 Commission: ₦7,500 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-14.png" alt="2 Days" />
            </div>
            <h4>Local SMEs | NGOs (2 Days)</h4>
            <div className="package-price">₦250,000</div>
            <div className="package-commission">💰 Commission: ₦12,500 (5%)</div>
          </div>
          <div className="package-card">
            <div className="package-image">
              <img src="/images/affiliates/affiliate-15.png" alt="3 Days" />
            </div>
            <h4>Local SMEs | NGOs (3 Days)</h4>
            <div className="package-price">₦300,000</div>
            <div className="package-commission">💰 Commission: ₦15,000 (5%)</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="affiliate-page">
      {/* Hero Section */}
      <div className="affiliate-hero">
        <div className="affiliate-hero-overlay"></div>
        <div className="affiliate-hero-content">
          <span className="affiliate-hero-badge">AFFILIATE PROGRAM</span>
          <h1>Earn 5% Commission</h1>
          <p>
            Join the AICIS 2026 Affiliate Program and earn 5% commission on every successful referral. 
            Promote Africa's premier infrastructure, climate change, and investment summit to your network.
          </p>
          <div className="affiliate-hero-stats">
            <div className="stat-item">
              <span className="stat-number">5%</span>
              <span className="stat-label">Commission Rate</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">₦7.65M</span>
              <span className="stat-label">Max Package Value</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">15</span>
              <span className="stat-label">Packages</span>
            </div>
          </div>
          {!isLoggedIn && (
            <div className="hero-buttons">
              <button onClick={() => setShowSignUp(true)} className="affiliate-hero-btn primary">
                Sign Up Now →
              </button>
              <button onClick={() => setShowLogin(true)} className="affiliate-hero-btn secondary">
                Login
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="affiliate-tabs">
        <div className="affiliate-tabs-container">
          <button
            className={`affiliate-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`affiliate-tab ${activeTab === 'packages' ? 'active' : ''}`}
            onClick={() => setActiveTab('packages')}
          >
            Packages
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="affiliate-content">
        <div className="affiliate-content-inner">
          {isLoggedIn ? (
            renderDashboard()
          ) : (
            <>
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'packages' && renderPackages()}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showLogin && renderLoginModal()}
      {showSignUp && renderSignUpModal()}

      {/* Footer */}
      <div className="affiliate-footer">
        <div className="affiliate-footer-inner">
          <span>AICIS 2026, Abuja Nigeria</span>
          <span>© ABVIG 2024. All rights reserved.</span>
        </div>
      </div>
    </div>
  );
};

export default Affiliate;